import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, interval } from 'rxjs';
import { environment } from '@environments/environment';
import { MobileBridgeService } from '@services/mobile-bridge.service';

@Injectable({
  providedIn: 'root'
})
export class PwaService {
  isIosDevice = false;
  pwaMetaData: {
    deferredPrompt: any;
    showInstallButton: boolean;
  } = {
    deferredPrompt: null,
    showInstallButton: false
  };
  private readonly pwaHideDuration = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

  constructor(
    @Inject(PLATFORM_ID) private readonly platform: Object,
    private readonly swUpdate: SwUpdate,
    private readonly mbs: MobileBridgeService
  ) {
    if (isPlatformBrowser(this.platform) && this.swUpdate.isEnabled && this.isRunningAsPWA()) {
      // Check for updates every 6 hours
      interval(6 * 60 * 60 * 1000).subscribe(() => this.checkForUpdate());

      // Listen for available updates
      this.swUpdate.versionUpdates
        .pipe(
          filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
        )
        .subscribe(() => {
          // Auto-update without user prompt
          this.promptUpdate();
        });
    }
  }

  /**
   * Checks if the application is running as a PWA (Progressive Web App)
   */
  private isRunningAsPWA(): boolean {
    if (!isPlatformBrowser(this.platform) || !window) {
      return false;
    }
    
    // Check if running in standalone mode (PWA)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                        (window?.navigator as any).standalone ||
                        window.location.search.includes('source=pwa');
    
    return isStandalone;
  }

  initPwaData() {
    if (isPlatformBrowser(this.platform) && window) {
      const userAgent = window?.navigator?.userAgent.toLowerCase();
      this.isIosDevice = /iphone|ipad|ipod/.test(userAgent);

      // Check if PWA is installed
      const dismissedAt = localStorage.getItem('pwa-close');
      const now = new Date().getTime();
      const isStandalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        (window?.navigator as any).standalone;
      if (isStandalone) {
        this.pwaMetaData.showInstallButton = false; // Hide button if app is already installed
      } else if (
        !dismissedAt ||
        now - Number(dismissedAt) > this.pwaHideDuration
      ) {
        if (this.isIosDevice) {
          this.pwaMetaData.showInstallButton = true;
          return;
        }
        // Listen for the install prompt
        window.addEventListener('beforeinstallprompt', (event: any) => {
          event.preventDefault();
          this.pwaMetaData.deferredPrompt = event;
          this.pwaMetaData.showInstallButton = true; // Show button only if app is not installed
        });

        // Hide button when app is installed
        window.addEventListener('appinstalled', () => {
          this.pwaMetaData.showInstallButton = false;
          this.pwaMetaData.deferredPrompt = null;
        });
      }
    }
  }
  closePwaBar() {
    if (isPlatformBrowser(this.platform)) {
      this.pwaMetaData.showInstallButton = false;
      localStorage.setItem('pwa-close', new Date()?.getTime()?.toString());
    }
  }

  installPWA() {
    if (isPlatformBrowser(this.platform) && this.pwaMetaData.deferredPrompt) {
      this.pwaMetaData.deferredPrompt.prompt();
      this.pwaMetaData.deferredPrompt.userChoice.then(() => {
        this.pwaMetaData.deferredPrompt = null;
        this.pwaMetaData.showInstallButton = false;
      });
    }
  }

  async checkForUpdate(): Promise<void> {
    if (!this.swUpdate.isEnabled) {
      return;
    }
    await this.swUpdate.checkForUpdate();
  }

  promptUpdate(): Promise<void> {
    return this.swUpdate.activateUpdate().then(() => {
      // Only reload if running in PWA mode and not in mobile app
      if (environment.production && !this.mbs.isMobileApp && this.isRunningAsPWA()) {
        window.location.reload();
      }
    });
  }
}
